import React, { useEffect, useState } from 'react';
import { SypacBadge } from '@sypac/component-library-react';
import { useTranslate } from '@tolgee/react';
import {
	getStateColor,
	StatusColors,
	StatusInvoiceColors,
	StatusIssuesColors,
	StatusMultiple,
	StatusMultipleExcluded,
	StatusTruckColors,
} from '../../constants/order-status.constants';
import { StatusClock } from '../../assets/StatusClock';
import { StatusBadgeProps } from './StatusBadge.interface';
import { Tooltip } from 'react-tooltip';
import { CheckmarkCircle } from '../../assets/CheckmarkCircle';
import { OrderTarget } from '../OrdersTable/interfaces/OrderStatus.interface';

const StatusBadge: React.FC<StatusBadgeProps> = ({
	status,
	isTruckStatus,
	deliveryIncluded,
	isInvoiceStatus,
	isIssueStatus,
	isState,
	target,
}) => {
	const { t } = useTranslate();
	const [currentStatus, setCurrentStatus] = useState(status);
	const [nextStatus, setNextStatus] = useState('');
	const [animate, setAnimate] = useState('');

	useEffect(() => {
		if (status !== currentStatus) {
			setNextStatus(status);
			setAnimate('animate-slide-out');
			setTimeout(() => {
				setCurrentStatus(status);
				setNextStatus('');
				setAnimate('');
			}, 600);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const getStatusProps = (status: string) => {
		if (target === OrderTarget.producer) {
			if (status === 'loaded') {
				return { label: t('statuses.loaded', 'Loaded'), color: 'green' };
			}
			// Before loading, the seller's truck card follows the same driver
			// sequence as the other roles so it agrees with the step title.
			const truckStep = deliveryIncluded ? StatusMultiple[status] : undefined;
			return {
				label: truckStep
					? t(truckStep.label)
					: t('statuses.pendingLoading', 'Pending loading'),
				color: truckStep?.color || 'yellow',
			};
		}
		if (target === OrderTarget.transporter) {
			return {
				label: t(StatusTruckColors[status]?.label),
				color: StatusTruckColors[status]?.color,
			};
		}
		if (isInvoiceStatus) {
			return {
				label: t(StatusInvoiceColors[status]?.label),
				color: StatusInvoiceColors[status]?.color,
			};
		}
		if (isIssueStatus) {
			return {
				label: t(StatusIssuesColors[status]?.label),
				color: StatusIssuesColors[status]?.color,
			};
		}
		if (isState) {
			const state = getStateColor(status);
			return {
				label: t(state.label),
				color: state.color,
			};
		}
		return isTruckStatus
			? deliveryIncluded
				? {
						label: t(
							StatusMultiple[status]?.label ||
								'transportSolution.awaitingConfirmation',
						),
						color: StatusMultiple[status]?.color || 'gray',
				  }
				: {
						label: t(
							StatusMultipleExcluded[status]?.label ||
								StatusMultipleExcluded.to_do.label,
						),
						color:
							StatusMultipleExcluded[status]?.color ||
							StatusMultipleExcluded.to_do.color,
				  }
			: {
					label: t(StatusColors[status]?.label || StatusColors.created.label),
					color: StatusColors[status]?.color || 'gray',
			  };
	};

	const { label: currentLabel, color: currentBgColor } =
		getStatusProps(currentStatus);
	const { label: nextLabel, color: nextBgColor } = getStatusProps(nextStatus);

	return isTruckStatus || isInvoiceStatus || isIssueStatus ? (
		<SypacBadge color={currentBgColor} outlined={false}>
			<div className="flex w-fit gap-2.5 items-center text-sm font-medium whitespace-nowrap cursor-auto">
				{(target === OrderTarget.producer && status === 'loaded') ||
				[
					'completed',
					'pending_payment',
					'paid',
					'available',
					'resolve',
					'ignore',
				].includes(status) ? (
					<CheckmarkCircle />
				) : (
					<StatusClock />
				)}
				<p className="m-0 mr-[5px] flex-1 text-center">{currentLabel}</p>
			</div>
		</SypacBadge>
	) : (
		<div className="flex overflow-hidden w-fit">
			<SypacBadge color={currentBgColor} outlined={false}>
				<div
					data-tooltip-id="status-badge"
					data-tooltip-content={
						currentStatus === 'created'
							? t(
									'orderStepper.simpleOrderIsBeingReviewed',
									'Your order is being reviewed',
							  )
							: ''
					}
					className={`min-w-[132px] h-[24px] flex gap-2 items-center text-sm font-medium whitespace-nowrap ${animate} cursor-auto box-border`}
				>
					<StatusClock color="#FFFFFF" />
					<p className="flex-1 m-0 text-center mt-[2px]">{currentLabel}</p>
				</div>
			</SypacBadge>
			<SypacBadge
				color={nextBgColor}
				outlined={false}
				className={`${nextStatus ? 'visible' : 'hidden'}`}
			>
				<div
					data-tooltip-id="status-badge"
					data-tooltip-content={
						currentStatus === 'created' ? 'Your order is being reviewed' : ''
					}
					className={`flex justify-between items-center text-sm font-medium whitespace-nowrap min-w-[132px] ${animate} cursor-auto`}
				>
					<StatusClock color="#FFFFFF" />
					<p className="m-0 flex-1 text-center">{nextLabel}</p>
				</div>
			</SypacBadge>
			{(currentStatus === 'created' || nextStatus === 'created') && (
				<Tooltip
					place="top"
					id="status-badge"
					style={{
						backgroundColor: '#E8E8E8',
						color: '#000000',
					}}
				/>
			)}
		</div>
	);
};

export default StatusBadge;
